import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Calculator, Minus, Plus, RotateCcw, Send, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LiveCounter } from '@/components/initiative/Counter';
import {
  totalGrants,
  type SponsorBreakdown,
} from '@/data/mock/initiative';

type SourceKey = keyof SponsorBreakdown;

const SOURCE_ORDER: SourceKey[] = ['bihub', 'bilbao', 'iknow', 'championsHub'];

/** Slider visual max — used only for the range slider scale, not as a hard cap. */
const SLIDER_VISUAL_MAX: Record<SourceKey, number> = {
  bihub: 50,
  bilbao: 50,
  iknow: 50,
  championsHub: 100,
};

const LOGOS: Record<SourceKey, string | null> = {
  bihub: null,
  bilbao: '/logos/ac-football-center.png',
  iknow: '/logos/ekkono.png',
  championsHub: '/logos/champions-hub-horizontal-light.png',
};

const EMPTY: SponsorBreakdown = { bihub: 0, bilbao: 0, iknow: 0, championsHub: 0 };

const PRESETS: { id: 'starter' | 'balanced' | 'lead'; mix: SponsorBreakdown }[] = [
  { id: 'starter', mix: { bihub: 0, bilbao: 5, iknow: 5, championsHub: 15 } },
  { id: 'balanced', mix: { bihub: 2, bilbao: 10, iknow: 10, championsHub: 28 } },
  { id: 'lead', mix: { bihub: 5, bilbao: 20, iknow: 20, championsHub: 55 } },
];

interface SponsorCalculatorProps {
  onApply: (mix: SponsorBreakdown) => void;
}

export function SponsorCalculator({ onApply }: SponsorCalculatorProps) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const lang = isRTL ? 'ar' : 'en';

  const [mix, setMix] = useState<SponsorBreakdown>(EMPTY);

  const grants = useMemo(() => totalGrants(mix), [mix]);
  const isEmpty = grants === 0;

  const updateOne = (key: SourceKey, next: number) => {
    const clamped = Math.max(0, Math.floor(next || 0));
    setMix((prev) => ({ ...prev, [key]: clamped }));
  };

  const reset = () => setMix(EMPTY);
  const applyPreset = (m: SponsorBreakdown) => setMix(m);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative overflow-hidden rounded-3xl border-2 border-accent/40 bg-gradient-to-br from-card via-card/95 to-accent/5 backdrop-blur-xl p-6 sm:p-8 lg:p-10 shadow-2xl shadow-accent/5"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Editorial backdrop */}
      <img
        aria-hidden
        src="/img/initiative/calculator-backdrop.webp"
        alt=""
        className="absolute inset-0 h-full w-full object-cover opacity-40 pointer-events-none"
        loading="lazy"
      />
      {/* Atmosphere */}
      <motion.div
        aria-hidden
        animate={{ scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-accent/15 blur-3xl pointer-events-none"
      />
      <motion.div
        aria-hidden
        animate={{ scale: [1.1, 1, 1.1], opacity: [0.25, 0.5, 0.25] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 3 }}
        className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-primary/15 blur-3xl pointer-events-none"
      />

      <div className="relative flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-10">
        {/* Header */}
        <div className="lg:w-72 lg:flex-shrink-0">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/30 mb-5">
            <Calculator className="h-3.5 w-3.5 text-accent" />
            <span className="text-[11px] font-mono-mark uppercase tracking-widest text-accent">
              {t('initiative.sponsor.calculator.eyebrow')}
            </span>
          </div>
          <h3
            className={cn(
              'font-display font-black uppercase leading-[0.95] text-3xl sm:text-4xl text-foreground',
              isRTL && 'font-display-ar normal-case leading-[1.15]'
            )}
          >
            {t('initiative.sponsor.calculator.title')}
          </h3>
          <p className="mt-4 font-editorial-italic text-base text-foreground/70 leading-relaxed">
            "{t('initiative.sponsor.calculator.subtitle')}"
          </p>

          {/* Presets */}
          <div className="mt-7">
            <div className="text-[10px] font-mono-mark uppercase tracking-widest text-muted-foreground mb-3">
              ※ {t('initiative.sponsor.calculator.presetsLabel')}
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <motion.button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.mix)}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                  className="px-3.5 py-1.5 text-xs font-semibold rounded-full bg-surface-1 border border-border/60 hover:border-accent hover:text-accent transition-colors"
                >
                  {t(`initiative.sponsor.calculator.presets.${p.id}`)}
                </motion.button>
              ))}
              <motion.button
                type="button"
                onClick={reset}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-3 w-3" />
                {t('initiative.sponsor.calculator.reset')}
              </motion.button>
            </div>
          </div>
        </div>

        {/* Sliders / inputs */}
        <div className="flex-1 min-w-0 space-y-4">
          {SOURCE_ORDER.map((key) => {
            const value = mix[key];
            const sliderMax = Math.max(SLIDER_VISUAL_MAX[key], value);
            const pct = sliderMax > 0 ? Math.min(100, (value / sliderMax) * 100) : 0;
            const logo = LOGOS[key];

            return (
              <div
                key={key}
                className="rounded-xl border border-border/60 bg-surface-1/60 p-4 sm:p-5"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-12 flex items-center justify-center flex-shrink-0">
                      {logo ? (
                        <img
                          src={logo}
                          alt={t(`initiative.sponsor.breakdownLabels.${key}`)}
                          className="max-h-7 max-w-full object-contain"
                        />
                      ) : (
                        <span className="font-display font-black text-[10px] tracking-tight text-accent border border-accent/40 bg-accent/10 px-1.5 py-0.5 rounded">
                          BiHUB
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground truncate">
                        {t(`initiative.sponsor.breakdownLabels.${key}`)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {t('initiative.sponsor.calculator.perScholarship')}
                      </div>
                    </div>
                  </div>

                  {/* Stepper */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <motion.button
                      type="button"
                      aria-label="decrease"
                      onClick={() => updateOne(key, value - 1)}
                      disabled={value <= 0}
                      whileTap={{ scale: 0.9 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                      className="h-9 w-9 rounded-lg border border-border/60 hover:border-accent hover:text-accent disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                    >
                      <Minus className="h-4 w-4" />
                    </motion.button>
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={value}
                      onChange={(e) => updateOne(key, Number(e.target.value))}
                      className="h-9 w-16 text-center text-base font-bold rounded-lg border border-border/60 bg-background num-tabular focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition-all"
                    />
                    <motion.button
                      type="button"
                      aria-label="increase"
                      onClick={() => updateOne(key, value + 1)}
                      whileTap={{ scale: 0.9 }}
                      whileHover={{ scale: 1.05 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                      className="h-9 w-9 rounded-lg border border-border/60 hover:border-accent hover:text-accent flex items-center justify-center transition-colors"
                    >
                      <Plus className="h-4 w-4" />
                    </motion.button>
                  </div>
                </div>

                {/* Slider — visual scale only; type any number above */}
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={0}
                    max={sliderMax}
                    value={value}
                    onChange={(e) => updateOne(key, Number(e.target.value))}
                    className="flex-1 h-1.5 rounded-full appearance-none cursor-pointer bg-border accent-accent"
                    style={{
                      background: `linear-gradient(${
                        isRTL ? '-90deg' : '90deg'
                      }, hsl(var(--accent)) 0%, hsl(var(--accent)) ${pct}%, hsl(var(--border)) ${pct}%, hsl(var(--border)) 100%)`,
                    }}
                  />
                  <span className="text-sm font-bold text-foreground num-tabular min-w-[3rem] text-end flex-shrink-0">
                    × <LiveCounter value={value} />
                  </span>
                </div>
              </div>
            );
          })}

          {/* Totals + CTA */}
          <motion.div
            layout
            transition={{ type: 'spring', stiffness: 200, damping: 24 }}
            className={cn(
              'relative mt-2 rounded-2xl border-2 p-5 sm:p-6 overflow-hidden transition-colors',
              isEmpty
                ? 'border-dashed border-border/60 bg-surface-1/40'
                : 'border-accent bg-gradient-to-br from-accent/10 via-accent/5 to-transparent'
            )}
          >
            {!isEmpty && (
              <motion.div
                aria-hidden
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-12 -right-12 h-40 w-40 rounded-full bg-accent/30 blur-3xl pointer-events-none"
              />
            )}

            <div className="relative grid grid-cols-2 sm:grid-cols-3 gap-5 items-end">
              <div>
                <div className="text-[10px] font-mono-mark uppercase tracking-widest text-muted-foreground">
                  {t('initiative.sponsor.calculator.totalScholarships')}
                </div>
                <div className="mt-1.5 font-display font-black text-4xl lg:text-5xl text-foreground num-tabular leading-none">
                  <LiveCounter value={grants} />
                </div>
              </div>
              <div className="sm:col-span-1">
                <div className="text-[10px] font-mono-mark uppercase tracking-widest text-muted-foreground">
                  {t('initiative.sponsor.priceLabel')}
                </div>
                <div
                  className={cn(
                    'mt-1.5 font-display font-black text-2xl lg:text-3xl leading-tight',
                    isEmpty
                      ? 'text-foreground/30'
                      : 'bg-gradient-to-b from-accent via-accent to-accent/70 bg-clip-text text-transparent'
                  )}
                >
                  {t('initiative.sponsor.priceOnRequest')}
                </div>
                <div className="text-[10px] font-mono-mark uppercase tracking-widest text-muted-foreground mt-1.5">
                  {t('initiative.sponsor.priceContactSales')}
                </div>
              </div>
              <div className="col-span-2 sm:col-span-1 sm:flex sm:justify-end">
                <motion.div
                  whileHover={!isEmpty ? { scale: 1.02 } : undefined}
                  whileTap={!isEmpty ? { scale: 0.97 } : undefined}
                  transition={{ type: 'spring', stiffness: 400, damping: 18 }}
                  className="w-full sm:w-auto"
                >
                  <Button
                    type="button"
                    size="lg"
                    className="w-full sm:w-auto gap-2 btn-glow font-bold uppercase tracking-wider text-sm h-12"
                    disabled={isEmpty}
                    onClick={() => onApply(mix)}
                  >
                    <Send className="h-4 w-4" />
                    {t('initiative.sponsor.calculator.applyCta')}
                  </Button>
                </motion.div>
              </div>
            </div>

            <AnimatePresence>
              {!isEmpty && (
                <motion.div
                  key="composition"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                  className="relative overflow-hidden"
                >
                  <div className="mt-5 pt-4 border-t border-accent/20 flex items-start gap-2">
                    <Sparkles className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-foreground/80 leading-relaxed">
                      {t('initiative.sponsor.calculator.composition', {
                        bihub: mix.bihub,
                        bilbao: mix.bilbao,
                        iknow: mix.iknow,
                        championsHub: mix.championsHub,
                      })}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}
